import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import { noop } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import classnames from 'classnames';

import { CmsEntities } from 'client/data/models/cms';
import { MpgParams } from 'site-modules/shared/utils/mpg/params';

import {
  CARDS_TYPE_MAP,
  CarNewsArticlesGrid,
} from 'site-modules/editorial/components/car-news/car-news-articles-grid/car-news-articles-grid';
import { CardsCarousel } from 'site-modules/editorial/components/car-news/cards-carousel/cards-carousel';
import { Spotlight } from 'site-modules/editorial/components/car-news/spotlight/spotlight';
import { VideoCard } from 'site-modules/editorial/components/car-news/video-card/video-card';
import { LeaderboardInfographic } from 'site-modules/editorial/components/leaderboard-infographic/leaderboard-infographic';
import { EvIncentivesModule } from 'site-modules/editorial/components/type-buying-guide/ev-incentives-module/ev-incentives-module';
import { ResearchByType } from 'site-modules/editorial/components/type-buying-guide/research-by-type/research-by-type';
import { EvMpg } from 'site-modules/editorial/components/type-buying-guide/ev-mpg/ev-mpg';
import { Link } from 'site-modules/shared/components/link/link';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NewsCard } from 'site-modules/editorial/components/car-news/news-card/news-card';
import { ComparatorPreviewModule } from 'site-modules/editorial/components/type-buying-guide/comparator-module/comparator-preview-module';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { TopRatedModule } from 'site-modules/editorial/components/type-buying-guide/top-rated-module/top-rated-module';
import { ExcoVideoViewer } from 'site-modules/shared/components/exco-video/exco-video-viewer';

import { RENDER_TYPE } from 'client/site-modules/editorial/utils/type-buying-guide-constants';

export function filterChildrenFromCardsCarousel(child, { isMobile } = {}) {
  return child.id !== 'spotlight-video' && !(isMobile && child.hasMetadata('adType'));
}

export function ResearchEntry({
  type,
  entry,
  isMobile = false,
  isLazyLoadImages = false,
  setVideoModalData = noop,
  params = null,
  hasResearchByTypeEntry = false,
  firstArticleTitle = null,
}) {
  const renderType = entry.metadata('renderType').value();
  let renderEntry;
  const { title } = entry;

  switch (renderType) {
    case RENDER_TYPE.ARTICLES_GRID: {
      const { moreButtonText, moreButtonUrl, htmlContent } = entry.getAllMetadata();
      renderEntry = (
        <Container className="mb-2_5">
          {!!title && (
            <h2
              className={classnames('heading-3', {
                'mb-2': !htmlContent,
              })}
            >
              {title}
            </h2>
          )}
          {!!htmlContent && (
            <ContentFragment componentToUse="p" classes="mb-2">
              {htmlContent}
            </ContentFragment>
          )}
          <CarNewsArticlesGrid
            content={entry}
            isMobile={isMobile}
            isLazyLoadImages={isLazyLoadImages}
            forceLayout={isMobile ? [[CARDS_TYPE_MAP.inline]] : undefined}
          />
          {moreButtonText && moreButtonUrl && (
            <div className="text-center mt-2">
              <Button
                tag={Link}
                to={moreButtonUrl}
                color="blue-50"
                className="text-transform-none size-16 px-2_5 py-0_75"
              >
                {moreButtonText}
              </Button>
            </div>
          )}
          {!hasResearchByTypeEntry && title === firstArticleTitle && <ExcoVideoViewer marginX="mx-auto" />}
        </Container>
      );
      break;
    }
    case RENDER_TYPE.ARTICLES_GRID_CAROUSEL: {
      renderEntry = (
        <Container className="mb-2_5">
          {!!title && <h2 className="heading-3 mb-2">{title}</h2>}
          {entry.children().length > 3 ? (
            <CardsCarousel
              content={entry}
              CardComponent={NewsCard}
              isMobile={isMobile}
              forceCarousel={isMobile}
              showArrows={!isMobile}
              centerMode={false}
              infinite={isMobile}
              slidesToShowDesktop={3}
              slidesToShowMobile={1.5}
              slidesToScrollMobile={1}
              initialSlide={0}
            />
          ) : (
            <CarNewsArticlesGrid
              content={entry}
              isMobile={isMobile}
              isLazyLoadImages={isLazyLoadImages}
              forceLayout={isMobile ? [[CARDS_TYPE_MAP.inline]] : undefined}
            />
          )}
        </Container>
      );
      break;
    }
    case RENDER_TYPE.RESEARCH_BY_TYPE: {
      renderEntry = (
        <Fragment>
          <ResearchByType content={entry} className="mb-2_5" isMobile={isMobile} />
          <Container>
            <ExcoVideoViewer marginX="mx-auto" />
          </Container>
        </Fragment>
      );
      break;
    }
    case RENDER_TYPE.VIDEOS: {
      const spotlightVideo = entry.child('spotlight-video');
      renderEntry = (
        <Container className="my-3 mt-md-0 mb-md-2_5">
          {!!title && <h2 className="heading-3 mb-2">{title}</h2>}
          {!!spotlightVideo && (
            <div style={{ marginBottom: isMobile ? '2rem' : '5.875rem' }}>
              <Spotlight
                content={spotlightVideo}
                openVideoModal={setVideoModalData}
                isLazyLoadImages={isLazyLoadImages}
              />
            </div>
          )}
          <CardsCarousel
            content={entry}
            CardComponent={VideoCard}
            openVideoModal={setVideoModalData}
            childrenFilter={filterChildrenFromCardsCarousel}
            isLazyLoadImages={isLazyLoadImages}
            isMobile={isMobile}
            showArrows={!isMobile}
            centerMode={isMobile}
          />
        </Container>
      );
      break;
    }
    case RENDER_TYPE.EV_INCENTIVES: {
      renderEntry = <EvIncentivesModule content={entry} className="mb-2_5" />;
      break;
    }
    case RENDER_TYPE.EV_MPG: {
      renderEntry = <EvMpg content={entry} params={params} isMobile={isMobile} />;
      break;
    }
    case RENDER_TYPE.EV_RANGE_LEADER_BOARD: {
      renderEntry = (
        <Container className="pt-3_5 mb-2_5 pos-r">
          <Link
            to={entry.metadata('link').value()}
            aria-label={entry.metadata('linkText').value()}
            tabIndex={-1}
            className="ev-range-leaderboard-top d-block mx-auto text-decoration-none mb-0_75"
          >
            <LeaderboardInfographic
              content={entry}
              widgetName="ev-range-leaderboard"
              className="ev-range-leaderboard"
              titleColorClassName="text-white"
              title="EV Range Leaderboard"
              barsMap={[
                { label: 'EPA estimate', key: 'estimate' },
                { label: 'Edmunds tested', key: 'tested' },
              ]}
              stepValue={100}
              chartValueMeasure="mi"
              hideEmbedBtn
              previewMode={3}
            />
          </Link>
          <div className="ev-range-leaderboard-bottom mx-auto text-end">
            {/* don't remove, need to fix a11y */}
            {/* todo - review PLAT-3859 disabling */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="" className="pos-a" style={{ opacity: 0 }} tabIndex={-1} aria-hidden>
              {''}
            </a>
            <Link to={entry.metadata('link').value()} className="text-blue-40">
              {entry.metadata('linkText').value()} <i className="icon-arrow-right4 small" aria-hidden />
            </Link>
          </div>
        </Container>
      );
      break;
    }
    case RENDER_TYPE.AD_UNIT: {
      renderEntry = (
        <Container className="mb-3_5">
          {!isMobile && (
            <div style={{ minHeight: '90px' }}>
              <NativeAd
                adNameWired="leaderc"
                nativeStyle="leaderboard"
                wiredOnly
                position={entry.metadata('wiredPosition').value('2')}
                dfpCollapse
                noRefresh
              />
            </div>
          )}
          {isMobile && (
            <MrectNative
              position={entry.metadata('mobilePosition').value('3')}
              refreshable={false}
              mobileOnly
              useMinHeight
              renderWhenViewable
              verticalOffset="100%"
            />
          )}
        </Container>
      );
      break;
    }
    case RENDER_TYPE.COMPARATOR_TABLE: {
      renderEntry = <ComparatorPreviewModule type={type} content={entry} isMobile={isMobile} className="mb-2_5" />;
      break;
    }
    case RENDER_TYPE.HTML: {
      const { title: htmlTitle } = entry;
      renderEntry = (
        <Container className="mb-2">
          <h2 className="heading-3">{htmlTitle}</h2>
          <ContentFragment classes="content-fragment html-content">
            {entry.metadata('htmlContent').value()}
          </ContentFragment>
        </Container>
      );
      break;
    }
    case RENDER_TYPE.TOP_RATED: {
      renderEntry = <TopRatedModule content={entry} isMobile={isMobile} className="pb-3_5 mb-3_5" />;
      break;
    }
    default: {
      break;
    }
  }

  if (!renderEntry) {
    return null;
  }

  return renderEntry;
}

ResearchEntry.propTypes = {
  type: PropTypes.string.isRequired,
  entry: CmsEntities.Content.isRequired,
  isMobile: PropTypes.bool,
  isLazyLoadImages: PropTypes.bool,
  setVideoModalData: PropTypes.func,
  params: MpgParams,
  hasResearchByTypeEntry: PropTypes.bool,
  firstArticleTitle: PropTypes.string,
};
